<template>
    <div
        :class="[
            vertical ? 'flex flex-col' : 'sm:grid sm:grid-cols-3 sm:gap-4',
            'input input_no_radius'
        ]"
    >
        <label
            class="flex items-center"
            :for="inputSlug"
        >
            {{ label }}

            <span
                v-if="optional"
                class="text-gray-500 italic text-xs ml-2"
            >
                Opcional
            </span>
        </label>

        <p
            v-if="description"
            class="text-gray-500 text-xs dark:text-gray-300"
        >
            {{ description }}
        </p>

        <Field
            v-slot="{ errorMessage, value }"
            v-model="inputValue"
            :name="inputName"
            :rules="rules"
            as="div"
            class="sm:col-span-2"
        >
            <div class="flex divide-x-0 dark:divide-neutral-600">
                <div
                    v-if="labelLeft.length > 0 "
                    class="border dark:border-neutral-600 inline-flex items-center px-3 rounded-l-md bg-gray-100 dark:bg-neutral-500 text-gray-500 dark:text-gray-300 sm:text-sm"
                >
                    {{ labelLeft }}
                </div>


                <template v-if="mask">
                    <input
                        :id="inputSlug"
                        v-mask="mask"
                        :maxlength="max"
                        :type="type"
                        :name="inputSlug"
                        :value="value"
                        :disabled="blockEdit"
                        :placeholder="placeholder"
                        :autocomplete="autocomplete"
                        :readonly="readOnly"
                        class="block w-full "
                        :class="[{ 'input_error': !!errorMessage }, inputClass, { 'text-center' : textCenter }]"
                        :min="min"
                        :max="max"
                        :style="[
                            labelRight.length > 0 || copyButton ? 'border-top-right-radius: 0px !important' : '',
                            labelRight.length > 0 || copyButton ? 'border-bottom-right-radius: 0px !important' : '',
                            labelLeft.length > 0 ? 'border-top-left-radius: 0px !important' : '',
                            labelLeft.length > 0 ? 'border-bottom-left-radius: 0px !important' : '',
                        ]"
                        @input="$emit('update:modelValue', $event.target.value)"
                        @blur="$emit('blur')"
                    >
                </template>

                <template v-else>
                    <div class="relative w-full">
                        <input
                            :id="inputSlug"
                            :type="getTypeInput(type)"
                            :name="inputSlug"
                            :value="value"
                            :disabled="blockEdit"
                            :placeholder="placeholder"
                            :autocomplete="autocomplete"
                            :readonly="readOnly"
                            class="block "
                            :class="[{ 'input_error': !!errorMessage }, inputClass, { 'text-center' : textCenter }]"
                            :min="min"
                            :max="max"
                            :maxlength="max"
                            :style="[
                                labelRight.length > 0 || copyButton ? 'border-top-right-radius: 0px !important' : '',
                                labelRight.length > 0 || copyButton ? 'border-bottom-right-radius: 0px !important' : '',
                                labelLeft.length > 0 ? 'border-top-left-radius: 0px !important' : '',
                                labelLeft.length > 0 ? 'border-bottom-left-radius: 0px !important' : '',
                            ]"
                            @input="$emit('update:modelValue', $event.target.value)"
                            @blur="$emit('blur')"
                        >
                        <span
                            v-if="type === 'password'"
                            class="absolute right-1 top-1 text-center items-center justify-center cursor-pointer"
                            @click="togglePassword"
                        >
                            <Unicon
                                :name="iconView"
                                class="text-gray-500 dark:text-gray-300 p-0.5 mt-0.5"
                            />
                        </span>
                    </div>
                </template>

                <div class="flex">
                    <button
                        v-if="copyButton"
                        :class="[
                            'border dark:border-neutral-600 inline-flex items-center px-3 rounded-r-md bg-gray-100 dark:bg-neutral-500 text-gray-500 dark:text-gray-300 sm:text-sm',
                            'chat_transition outline-none focus:border-chat-primary focus:border-2 focus-within:z-30 -ml-px'
                        ]"
                        @click.prevent.stop="copyToClipboard"
                    >
                        <Unicon
                            name="copy"
                            class="text-gray-500 dark:text-gray-300"
                        />
                    </button>
                </div>

                <div
                    v-if="labelRight.length > 0 "
                    :class="[
                        'border dark:border-neutral-600 inline-flex items-center px-3 rounded-r-md bg-gray-100 dark:bg-neutral-500 text-gray-500 dark:text-gray-300 sm:text-sm',
                    ]"
                >
                    {{ labelRight }}
                </div>
            </div>

            <span
                v-if="!!errorMessage"
                class="input_error_message"
            >
                {{ removerTextoAposTraco(errorMessage) }}
            </span>
            <p
                v-if="hint.length > 0"
                class="text-gray-500 text-xs dark:text-gray-300 mt-2 "
                :class="{ 'text-red-500': hintAlert }"
                v-html="hint"
            />
        </Field>
    </div>
</template>

<script>
    import { Field } from 'vee-validate'
    import { str_slug } from '@/utils/string'
    import { notify } from '@kyvg/vue3-notification'

    export default {
        name: 'InputFormHorizontal',

        components: { Field },

        props: {
            label: { type: String, default: '' },
            description: { type: String, default: '' },
            name: { type: String, default: '' },
            optional: { type: Boolean, default: false },
            initValue: { type: [String, Number], default: '' },
            type: { type: String, default: 'text' },
            placeholder: { type: String, default: '' },
            rules: { type: String, default: '' },
            blockEdit: { type: Boolean, default: false },
            vertical: { type: Boolean, default: false },
            min: { type: String, default: '' },
            max: { type: String, default: '' },
            labelRight: { type: String, default: '' },
            labelLeft: { type: String, default: '' },
            hint: { type: String, default: '' },
            autocomplete: { type: String, default: 'off' },
            copyButton: { type: Boolean, default: false },
            readOnly: { type: Boolean, default: false },
            textCenter: { type: Boolean, default: false },
            inputClass: { type: String, default: '' },
            mask: { type: [String, Array], default: '' },
            hintAlert: { type: Boolean, default: false },
        },

        emits: ['update:modelValue', 'blur'],

        data() {
            return {
                toggle: false,
                inputValue: this.initValue,
            }
        },

        computed: {
            inputSlug() {
                return str_slug(this.name) + str_slug(this.label)
            },
            viewPassword() {
                return this.toggle ? 'text' : 'password'
            },
            iconView() {
                return this.toggle ? 'eye-slash' : 'eye'
            },
            inputName() {
                return `${this.label}-${Math.floor(Math.random() * 1000)}`
            },

            inputMask() {
                if (this.mask) {
                    return this.mask
                }

                let mask = ''
                for (let i = 0; i < 255; i++) {
                    mask += 'X'
                }

                return mask
            },
        },

        watch: {
            initValue: {
                handler(val) {
                    this.inputValue = val
                },
            },
        },

        methods: {
            getTypeInput(type) {
                if (type === 'password') {
                    return this.viewPassword
                }
                return type
            },
            togglePassword() {
                this.toggle = !this.toggle
            },
            removerTextoAposTraco(text) {
                const result = text.split(' ').map(item => {
                    const index = item.indexOf('-')
                    if (index !== -1) {
                        return item.substring(0, index)
                    }
                    return item
                })
                return result.join(' ')


            },
            copyToClipboard() {
                const input = document.getElementById(this.inputSlug)
                input.select()
                document.execCommand('copy')

                notify({
                    title: 'Copiado',
                    text: 'O conteúdo foi copiado para sua área de transferência!',
                    type: 'success',
                    duration: 2000,
                })
            },
        },
    }
</script>
